import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Image, 
  File, 
  Folder, 
  Search, 
  Filter, 
  Download, 
  Share2, 
  Trash2,
  Upload,
  Grid3X3,
  List,
  Camera
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { scannerService, ScannedDocument } from '../../lib/scannerService';

interface MobileFile {
  id: string;
  name: string;
  type: 'document' | 'image' | 'folder' | 'other';
  size: string;
  modified: string;
  category: string;
}

export function MobileFiles() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [scannedDocuments, setScannedDocuments] = useState<ScannedDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Combine mock files with scanned documents
  const allFiles: MobileFile[] = [
    // Mock files
    {
      id: '1',
      name: 'Health Records.pdf',
      type: 'document',
      size: '2.4 MB',
      modified: '2 hours ago',
      category: 'health'
    },
    {
      id: '2',
      name: 'Travel Documents',
      type: 'folder',
      size: '12 files',
      modified: '1 day ago',
      category: 'travel'
    },
    {
      id: '3',
      name: 'Passport.jpg',
      type: 'image',
      size: '1.8 MB',
      modified: '3 days ago',
      category: 'travel'
    },
    {
      id: '4',
      name: 'Insurance Policy.pdf',
      type: 'document',
      size: '5.2 MB',
      modified: '1 week ago',
      category: 'legal'
    },
    {
      id: '5',
      name: 'Pet Vaccination Records',
      type: 'folder',
      size: '8 files',
      modified: '2 weeks ago',
      category: 'pets'
    },
    // Scanned documents
    ...scannedDocuments.map(doc => ({
      id: doc.id,
      name: doc.name,
      type: 'image' as const,
      size: doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(1)} MB` : 'Unknown',
      modified: new Date(doc.timestamp).toLocaleDateString(),
      category: 'scanner'
    }))
  ];

  const categories = [
    { id: 'all', label: 'All Files' },
    { id: 'scanner', label: 'Scanned' },
    { id: 'health', label: 'Health' },
    { id: 'travel', label: 'Travel' },
    { id: 'legal', label: 'Legal' },
    { id: 'pets', label: 'Pets' },
    { id: 'financial', label: 'Financial' }
  ];

  // Load scanned documents
  useEffect(() => {
    const loadScannedDocuments = async () => {
      try {
        setIsLoading(true);
        const documents = await scannerService.getScannedDocuments();
        setScannedDocuments(documents);
      } catch (err) {
        console.error('Failed to load scanned documents:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadScannedDocuments();
  }, []);

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'document':
        return <FileText size={20} className="text-blue-500" />;
      case 'image':
        return <Image size={20} className="text-green-500" />;
      case 'folder':
        return <Folder size={20} className="text-yellow-500" />;
      default:
        return <File size={20} className="text-gray-500" />;
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      health: 'bg-red-100 text-red-600',
      travel: 'bg-blue-100 text-blue-600',
      legal: 'bg-gray-100 text-gray-600',
      pets: 'bg-orange-100 text-orange-600',
      financial: 'bg-green-100 text-green-600'
    };
    return colors[category] || 'bg-gray-100 text-gray-600';
  };

  const filteredFiles = allFiles.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || file.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleFileAction = (action: string, fileId: string) => {
    console.log(`${action} file:`, fileId);
    // In real app, this would trigger the appropriate action
  };

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Files</h2>
          <p className="text-sm text-gray-600">{filteredFiles.length} files</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid3X3 size={16} />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List size={16} />
          </Button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="space-y-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category.id)}
              className="whitespace-nowrap"
            >
              {category.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Upload Button */}
      <Button className="w-full" onClick={() => navigate('/mobile/scanner')}>
        <Camera size={16} className="mr-2" />
        Scan Document
      </Button>

      {/* Files Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-2 gap-3">
          {filteredFiles.map((file) => (
            <Card
              key={file.id}
              className="p-3 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleFileAction('open', file.id)}
            >
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                  {getFileIcon(file.type)}
                </div>
                <div className="w-full">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">{file.size}</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs ${getCategoryColor(file.category)}`}>
                    {file.category}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredFiles.map((file) => (
            <Card
              key={file.id}
              className="p-3 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleFileAction('open', file.id)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  {getFileIcon(file.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {file.size} • {file.modified}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileAction('download', file.id);
                    }}
                  >
                    <Download size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileAction('share', file.id);
                    }}
                  >
                    <Share2 size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileAction('delete', file.id);
                    }}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredFiles.length === 0 && (
        <div className="text-center py-8">
          <FileText size={48} className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">No files found</p>
          <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="text-md font-semibold text-gray-900 mb-3">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate('/mobile/scanner')}
          >
            <Camera size={16} className="mr-2" />
            Scan Document
          </Button>
          <Button variant="outline" className="w-full">
            <Folder size={16} className="mr-2" />
            New Folder
          </Button>
        </div>
      </div>
    </div>
  );
} 